import React from 'react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Add from '@material-ui/icons/Add';
import Description from '@material-ui/icons/Description';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { colors } from '@skyslope/mache';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
// eslint-disable-next-line import/no-named-as-default -- posthog-js re-exports its own default as a named `posthog` too
import posthog from 'posthog-js';
import { useHistory } from 'react-router';
import { useGetUserAndAccountSettings } from '@/lib/api/_hooks';
import type { ITemplateMetadata } from '@/store/templateManagement/types';
import useUserInfo from '../auth/useUserInfo';
import { type LaunchDarklyFlags, withLaunchDarkly } from '../common/launchDarkly';
import FooterLinks from '../components/common/FooterLinks';
import ManagementHeader from '../components/common/ManagementHeader';
import NoResultsState from '../components/common/NoResultsState';
import Search from '../components/common/Search';
import { StyledMenu, StyledMenuItem } from '../components/common/StyledMenu';
import Table, { type IColumn } from '../components/common/Table';
import { ConfirmationModal } from '../components/templateManagement/DeleteTemplateConfirmationModal';
import EmptyState from '../components/templateManagement/EmptyState';
import { TaggingFilters } from '../components/templateManagement/TaggingFilters';
import { HeaderContext } from '../context/header-context';
import { ResourceNames } from '../lib/constants';
import { useDispatch, useShallowSelector } from '../lib/reduxHooks';
import useDebounce from '../lib/useDebounce';
import * as pageFrameActions from '../store/pageFrame/actions';
import * as actions from '../store/templateManagement/actions';

dayjs.extend(advancedFormat);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: 1000,
    width: '100%',
    margin: `${theme.spacing(7)}px ${theme.spacing(2)}px 0`,
    display: 'flex',
    flexDirection: 'column',
  },
  emptyStateRoot: {
    maxWidth: 1200,
    width: '100%',
    margin: `${theme.spacing(7)}px ${theme.spacing(2)}px 0`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  tableControls: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  templateCount: {
    display: 'flex',
    alignItems: 'center',
  },
  templateCountIcon: {
    marginRight: theme.spacing(1),
    color: colors.grey[500],
  },
  plusIcon: {
    margin: '-2px 0px -2px -6px',
    height: 18,
  },
  hotdogButton: {
    padding: 6,
    margin: -7,
  },
}));

export interface ITagFilters {
  [tagId: string]: string;
}

function TemplateManagement({ flags }: { flags: LaunchDarklyFlags }) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();

  const store = useShallowSelector((state) => ({
    templates: state.templateManagement.templates,
    search: state.templateManagement.search,
    isFetching: state.templateManagement.isFetching,
    createdId: state.templateManagement.createdId,
    isLoading: state.pageFrame.loading,
    newEnvelopeId: state.templateManagement.newEnvelopeId,
  }));

  const settings = useGetUserAndAccountSettings();
  const isTemplateTaggingEnabled = settings.data?.account.template?.tagging?.enabled;

  const headerContext = React.useContext(HeaderContext);
  const [search, setSearch] = React.useState('');
  const [tagFilters, setTagFilters] = React.useState<ITagFilters>({});
  const debouncedSearch = useDebounce(search, 100);
  const [openActionsId, setOpenActionsId] = React.useState('');
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const [filteredTemplates, setFilteredTemplates] = React.useState<ITemplateMetadata[]>([]);
  const rootClass = !store.isFetching && store.templates.length === 0 ? classes.emptyStateRoot : classes.root;
  const enableTemplateSharing = flags?.['enable-template-sharing'];
  const { oktaUserId, impersonatedPrimeUserGuid, isPrimeAuthenticated } = useUserInfo() ?? {};

  React.useEffect(() => {
    headerContext.setContent(
      <ManagementHeader
        resource={ResourceNames.TEMPLATE}
        flags={flags}
        userOrigin={settings.data?.account.userOrigin}
      />
    );
    return () => {
      headerContext.setContent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [settings.isFetched]);

  React.useEffect(() => {
    let { templates } = store;
    if (store.search) {
      const searchLower = store.search.toLowerCase();
      templates = templates.filter((t) => t.name.toLowerCase().includes(searchLower));
    }
    if (Object.keys(tagFilters).length) {
      templates = templates.filter((t) => {
        for (const tagId in tagFilters) {
          if (!t.tags.find((tag) => tag.tagTypeId === tagId && tag.value === tagFilters[tagId])) {
            return false;
          }
        }
        return true;
      });
    }
    if (oktaUserId) {
      for (const template of templates) {
        if (template.createdBy === oktaUserId) {
          template.createdByName = 'Me';
        }
      }
    }
    setFilteredTemplates(templates);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.templates.length, store.search, tagFilters, oktaUserId]);

  React.useEffect(() => {
    dispatch(actions.fetchTemplates());
    return () => {
      dispatch(actions.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  React.useEffect(() => {
    if (store.newEnvelopeId) {
      const url = `/envelopes/${store.newEnvelopeId}/documents`;
      history.push(url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.newEnvelopeId]);

  React.useEffect(() => {
    dispatch(actions.filterTemplates(search));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [debouncedSearch]);

  const tableColumns = React.useRef<IColumn[]>(
    (() => {
      const columns = [{ key: 'name', label: 'Name' }];
      const createdByCol = {
        key: 'createdByName',
        label: 'Created By',
      };
      const createdDateCol = {
        key: 'createdDate',
        label: 'Date Created',
        cell: (template: ITemplateMetadata) => (
          <Typography data-spec="createdDate-cell">{dayjs(template.createdDate).format('MM/DD/YY')}</Typography>
        ),
      };
      const editCol = {
        key: 'edit',
        align: 'right',
        label: '',
        cell: (template: ITemplateMetadata) => (
          <IconButton
            data-spec={`${template.id}-action-menu`}
            onClick={(e) => openMenu(e, template.id)}
            className={classes.hotdogButton}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      };

      columns.push(createdByCol, createdDateCol, editCol);
      return columns;
    })()
  );

  function openMenu(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, templateId: string) {
    setOpenActionsId(templateId);
    buttonRef.current = event.currentTarget;
  }

  function closeMenu() {
    setOpenActionsId('');
    buttonRef.current = null;
  }

  const [showDeleteTemplateModal, setShowDeleteTemplateModal] = React.useState(false);

  function toggleModal() {
    setShowDeleteTemplateModal(!showDeleteTemplateModal);
    if (showDeleteTemplateModal) {
      closeMenu();
    }
  }

  function deleteTemplate(templateId: string) {
    posthog.capture('template_deleted');
    dispatch(actions.deleteTemplate(templateId, true));
    toggleModal();
  }

  function createTemplate() {
    posthog.capture('template_created');
    dispatch(actions.createTemplate());
  }

  async function editTemplate(templateId: string) {
    const url = `/templates/${templateId}/${isTemplateTaggingEnabled ? 'info' : 'create'}`;
    history.push(url);
  }

  async function shareTemplate(templateId: string) {
    const url = `/templates/${templateId}/share`;
    history.push(url);
  }

  async function createEnvelopeFromTemplate(templateId: string) {
    posthog.capture('envelope_created_from_template');
    dispatch(pageFrameActions.setLoading(true));
    dispatch(actions.createEnvelopeFromTemplate(templateId, impersonatedPrimeUserGuid));
    closeMenu();
  }

  if (store.isLoading || !settings.isFetched) {
    return null;
  }

  if (store.createdId) {
    editTemplate(store.createdId);
  }

  function content() {
    if (filteredTemplates.length > 0) {
      return (
        <Table
          idKey="id"
          columns={tableColumns.current}
          rows={filteredTemplates}
          isLoading={store.isFetching}
          managementTable
        />
      );
    }
    if (search) {
      return <NoResultsState resource={ResourceNames.TEMPLATE} />;
    }
    return (
      <Table
        idKey="id"
        columns={tableColumns.current}
        rows={filteredTemplates}
        isLoading={store.isFetching}
        managementTable
      />
    );
  }

  const currentTemplate = filteredTemplates.find((template) => template.id === openActionsId);
  const userCreatedTemplate = currentTemplate?.createdByName?.toLowerCase() === 'me';

  return (
    <div className={rootClass}>
      {!store.isFetching && store.templates.length === 0 ? (
        <EmptyState data-spec="empty-template-management-state" />
      ) : (
        <>
          <Search onChange={setSearch} value={search} />
          {isTemplateTaggingEnabled ? <TaggingFilters tagFilters={tagFilters} setTagFilters={setTagFilters} /> : null}
          <div className={classes.tableControls}>
            <Typography variant="overline" className={classes.templateCount} data-spec="template-count">
              <Description className={classes.templateCountIcon} />
              {store.isFetching
                ? 'FETCHING TEMPLATES...'
                : `${filteredTemplates.length} TEMPLATE${filteredTemplates.length !== 1 ? 'S' : ''} FOUND ${
                    filteredTemplates.length < store.templates.length ? '(FILTERED)' : ''
                  }`}
            </Typography>
            <Button onClick={createTemplate} color="primary" variant="contained" data-spec="create-template-button">
              <Add className={classes.plusIcon} /> Create New
            </Button>
          </div>
          {content()}
          <StyledMenu
            anchorEl={buttonRef.current}
            open={Boolean(openActionsId)}
            onClose={closeMenu}
            keepMounted
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <StyledMenuItem data-spec="create-envelope" onClick={() => createEnvelopeFromTemplate(openActionsId)}>
              Create Envelope
            </StyledMenuItem>
            {userCreatedTemplate ? (
              <StyledMenuItem onClick={() => editTemplate(openActionsId)} data-spec="edit-button">
                Edit Template
              </StyledMenuItem>
            ) : null}
            {enableTemplateSharing && userCreatedTemplate && isPrimeAuthenticated ? (
              <StyledMenuItem onClick={() => shareTemplate(openActionsId)} data-spec="share-template">
                Share Template
              </StyledMenuItem>
            ) : null}
            {userCreatedTemplate ? (
              <StyledMenuItem onClick={toggleModal} data-spec="delete-button">
                Delete Template
              </StyledMenuItem>
            ) : null}
          </StyledMenu>
          {showDeleteTemplateModal ? (
            <ConfirmationModal
              data-spec="delete-template-modal"
              toggleModal={toggleModal}
              deleteTemplate={() => deleteTemplate(openActionsId)}
            />
          ) : null}
        </>
      )}
      <FooterLinks showSocialIcons />
    </div>
  );
}

const withLd = withLaunchDarkly(TemplateManagement);
export { withLd as TemplateManagement };
